import type { Variants } from 'motion/react';

// Shared motion presets, AI-Native and reduced-motion aware.
//
// Spread the fade* / popIn objects directly onto a <motion.*> element for the
// standard scroll-triggered reveal:
//   <motion.div {...fadeInUp}>...</motion.div>
// Use the *Variants (staggerContainer / messageReveal / staggerItem) with the
// `variants` prop. Override timing by adding a `transition` after the spread.
//
// `reducedMotion` is read once at module init — good enough for a static
// marketing site. Components running *infinite* loops (typing dots, floating
// chips, typewriter) should ALSO use Framer's useReducedMotion() hook so they
// react to a live OS change mid-session.

const prefersReduced = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const reducedMotion = prefersReduced();
const RM = reducedMotion;

export const EASE_OUT = [0.16, 1, 0.3, 1] as const; // expo-out "settle"
export const EASE_SOFT = [0.4, 0, 0.2, 1] as const; // material standard

export const D = {
  micro: 0.18,
  reveal: RM ? 0.2 : 0.5,
  hero: RM ? 0.2 : 0.7,
} as const;

export const fadeInUp = {
  initial: { opacity: 0, y: RM ? 0 : 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: D.reveal, ease: EASE_OUT },
};

export const fadeIn = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: D.reveal, ease: EASE_OUT },
};

export const fadeInLeft = {
  initial: { opacity: 0, x: RM ? 0 : -24 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: D.reveal, ease: EASE_OUT },
};

export const fadeInRight = {
  initial: { opacity: 0, x: RM ? 0 : 24 },
  whileInView: { opacity: 1, x: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: D.reveal, ease: EASE_OUT },
};

// Badges / pills / counters — small scale pop.
export const popIn = {
  initial: { opacity: 0, scale: RM ? 1 : 0.9 },
  whileInView: { opacity: 1, scale: 1 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: D.micro, ease: EASE_OUT },
};

// Parent/child pair for staggered list reveals (use with the `variants` prop).
export const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: RM ? 0 : 0.08, delayChildren: RM ? 0 : 0.05 },
  },
};

export const staggerFast: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: RM ? 0 : 0.05 },
  },
};

export const staggerItem: Variants = {
  hidden: { opacity: 0, y: RM ? 0 : 12 },
  visible: { opacity: 1, y: 0, transition: { duration: D.reveal, ease: EASE_OUT } },
};

// Chat-bubble entrance — slides up + settles from its side.
export const messageReveal: Variants = {
  hidden: { opacity: 0, y: RM ? 0 : 8, scale: RM ? 1 : 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: RM ? 0.2 : 0.28, ease: EASE_OUT },
  },
};

// Card hover lift — pair with whileHover={{ y: -6 }} transition={hoverLift}.
export const hoverLift = RM
  ? { duration: 0 }
  : ({ type: 'spring', stiffness: 300, damping: 24 } as const);

// Gentle infinite float for decorative chips. Spreads to nothing under RM.
export const floatLoop = RM
  ? {}
  : {
      animate: { y: [0, -6, 0] },
      transition: { duration: 3, repeat: Infinity, ease: 'easeInOut' as const },
    };
